import { useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCelebrationStore } from '@/stores/celebrationStore';
import { settingsApi } from '@/services/api';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  alpha: number;
  size: number;
  decay: number;
}

interface Firework {
  x: number;
  y: number;
  targetY: number;
  speed: number;
  color: string;
  exploded: boolean;
  particles: Particle[];
}

interface Confetti {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  color: string;
  angle: number;
  spin: number;
  sway: number;
}

const COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DDA0DD', // Plum
  '#98D8C8', // Mint
  '#F7DC6F', // Gold
  '#BB8FCE', // Purple
  '#85C1E9', // Sky
];

export function CelebrationOverlay() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const fireworksRef = useRef<Firework[]>([]);
  const lastLaunchRef = useRef(0);
  const confettiRef = useRef<Confetti[]>([]);

  const { isActive, variant, data, dismiss } = useCelebrationStore();
  const isChampion = variant === 'champion';

  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.getAll,
    enabled: isActive,
  });

  // Champion show runs for the full length of the Queen track; the dedicated
  // <audio> 'ended' handler dismisses it precisely, this is just a safety net
  // in case audio fails to load. Normal sales use the configurable setting.
  const CHAMPION_FALLBACK_SECONDS = 190; // song is ~182s + headroom
  const duration = isChampion
    ? CHAMPION_FALLBACK_SECONDS
    : settingsData?.settings?.fireworksDurationSeconds?.value ?? 10;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const playAudio = useCallback(() => {
    // Champion milestone audio is handled declaratively by a <audio> element in
    // the JSX below (more robust than imperative new Audio() across re-mounts).
    if (isChampion) return;

    if (!data) return;

    // If we have OpenAI-generated audio, play it through Web Audio with a
    // GainNode so we can amplify past the browser's HTMLAudioElement cap (1.0)
    // and make the celebration genuinely loud on dashboard TVs.
    if (data.audioBase64) {
      try {
        const audio = new Audio(`data:audio/mp3;base64,${data.audioBase64}`);
        audio.crossOrigin = 'anonymous';
        audio.volume = 1;
        audioRef.current = audio;

        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (Ctx) {
          const ctx = new Ctx();
          audioCtxRef.current = ctx;
          const source = ctx.createMediaElementSource(audio);
          const gain = ctx.createGain();
          gain.gain.value = 2.2; // 220% — punchy without clipping noticeably
          source.connect(gain).connect(ctx.destination);
          // Resume context if suspended (autoplay policies)
          if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
          }
        }

        audio.play().catch(err => console.error('Error playing audio:', err));
        return;
      } catch (error) {
        console.error('Error creating audio:', error);
      }
    }

    // Fallback to speech synthesis if no audio
    if (data.message && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(data.message);
      utterance.lang = 'he-IL';
      utterance.rate = 0.9;
      utterance.pitch = 1.1;
      utterance.volume = 1;

      const voices = window.speechSynthesis.getVoices();
      const hebrewVoice = voices.find(
        (v) => v.lang.startsWith('he') || v.lang.startsWith('iw')
      );
      if (hebrewVoice) {
        utterance.voice = hebrewVoice;
      }
      window.speechSynthesis.speak(utterance);
    }
  }, [data]);

  const createFirework = useCallback((canvas: HTMLCanvasElement): Firework => {
    const x = Math.random() * canvas.width * 0.8 + canvas.width * 0.1;
    return {
      x,
      y: canvas.height,
      targetY: Math.random() * canvas.height * 0.4 + canvas.height * 0.1,
      speed: 8 + Math.random() * 4,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      exploded: false,
      particles: [],
    };
  }, []);

  // Spawn a burst of confetti across the top of the screen (champion show only).
  const spawnConfetti = useCallback((canvas: HTMLCanvasElement, count: number) => {
    for (let i = 0; i < count; i++) {
      confettiRef.current.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * 60,
        vx: (Math.random() - 0.5) * 2,
        vy: 2 + Math.random() * 3,
        w: 6 + Math.random() * 8,
        h: 10 + Math.random() * 10,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.3,
        sway: Math.random() * Math.PI * 2,
      });
    }
  }, []);

  const explodeFirework = useCallback((firework: Firework) => {
    // Champion show gets fuller bursts than a normal sale celebration — but
    // kept bounded so particle counts can't snowball and freeze the main thread.
    const base = isChampion ? 110 : 80;
    const particleCount = base + Math.floor(Math.random() * 40);

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.2;
      const speed = 2 + Math.random() * 6;

      firework.particles.push({
        x: firework.x,
        y: firework.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color: Math.random() > 0.3 ? firework.color : COLORS[Math.floor(Math.random() * COLORS.length)],
        alpha: 1,
        size: 2 + Math.random() * 2,
        decay: 0.015 + Math.random() * 0.01,
      });
    }

    firework.exploded = true;
  }, [isChampion]);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Trail effect: fade old pixels toward transparent (not black) so the
    // background GIF remains visible underneath the canvas.
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';

    // Launch new fireworks periodically. The champion show launches more often
    // and in small bursts for a packed sky — but a hard cap on concurrent
    // fireworks keeps the particle count (and CPU) from snowballing.
    const now = Date.now();
    const maxFireworks = isChampion ? 16 : 12;
    const launchGap = isChampion ? 230 + Math.random() * 200 : 300 + Math.random() * 400;
    if (now - lastLaunchRef.current > launchGap && fireworksRef.current.length < maxFireworks) {
      const burst = isChampion ? 1 + Math.floor(Math.random() * 2) : 1;
      for (let i = 0; i < burst; i++) {
        fireworksRef.current.push(createFirework(canvas));
      }
      lastLaunchRef.current = now;
    }

    // Confetti rain — champion show only. Keep a steady stream falling.
    if (isChampion) {
      if (confettiRef.current.length < 120) {
        spawnConfetti(canvas, 3);
      }
      confettiRef.current = confettiRef.current.filter((c) => {
        c.sway += 0.05;
        c.x += c.vx + Math.sin(c.sway) * 1.2;
        c.y += c.vy;
        c.angle += c.spin;

        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(c.angle);
        ctx.fillStyle = c.color;
        ctx.fillRect(-c.w / 2, -c.h / 2, c.w, c.h);
        ctx.restore();

        return c.y < canvas.height + 30;
      });
    }

    // Update and draw fireworks
    fireworksRef.current = fireworksRef.current.filter((firework) => {
      if (!firework.exploded) {
        // Move firework up
        firework.y -= firework.speed;

        // Draw trail
        ctx.beginPath();
        ctx.arc(firework.x, firework.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = firework.color;
        ctx.fill();

        // Check if reached target
        if (firework.y <= firework.targetY) {
          explodeFirework(firework);
        }

        return true;
      } else {
        // Update particles
        let aliveParticles = 0;

        firework.particles.forEach((particle) => {
          if (particle.alpha <= 0) return;

          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.vy += 0.08; // Gravity
          particle.vx *= 0.99; // Air resistance
          particle.alpha -= particle.decay;

          if (particle.alpha > 0) {
            aliveParticles++;

            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size * particle.alpha, 0, Math.PI * 2);
            ctx.fillStyle = particle.color;
            ctx.globalAlpha = particle.alpha;
            ctx.fill();
            ctx.globalAlpha = 1;
          }
        });

        return aliveParticles > 0;
      }
    });

    animationRef.current = requestAnimationFrame(animate);
  }, [createFirework, explodeFirework, spawnConfetti, isChampion]);

  useEffect(() => {
    if (!isActive) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Clear canvas to fully transparent so the background GIF shows through
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    // Reset fireworks + confetti
    fireworksRef.current = [];
    confettiRef.current = [];
    lastLaunchRef.current = 0;

    // Start animation
    animate();

    // Play the audio after a short delay
    setTimeout(playAudio, 500);

    // Auto-clear after duration
    const timeout = setTimeout(() => {
      dismiss();
    }, duration * 1000);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      clearTimeout(timeout);
      // Stop audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      window.speechSynthesis.cancel();
    };
  }, [isActive, duration, animate, playAudio, dismiss]);

  if (!isActive) return null;

  if (isChampion) {
    return (
      <div
        className="fixed inset-0 z-50 pointer-events-auto cursor-pointer bg-black"
        onClick={dismiss}
      >
        {/* Queen anthem — declarative <audio> React fully owns. autoPlay starts
            it, onEnded ends the show exactly with the song, and unmount (on
            dismiss) stops it. The ref callback also calls play() as a belt-and-
            suspenders against autoplay timing quirks. */}
        <audio
          src="/champions.mp3"
          autoPlay
          onEnded={dismiss}
          ref={(el) => {
            if (el) {
              el.volume = 1;
              const p = el.play();
              if (p) p.catch(() => {});
            }
          }}
        />
        {/* Dedicated champion video — full-bleed, contained to avoid cropping */}
        <video
          src="/champions.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-contain"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 pointer-events-none" />
        {/* Fireworks canvas painted on top of the video */}
        <canvas ref={canvasRef} className="absolute inset-0" />

        {/* Blink animation for the milestone amount */}
        <style>{`@keyframes championBlink { 0%,49% { opacity: 1; } 50%,100% { opacity: 0.15; } }`}</style>

        {/* "We Are The Champions" title + blinking milestone amount, near the top */}
        <div className="absolute inset-x-0 top-0 flex flex-col items-center pointer-events-none px-4">
          <div className="mt-10 text-6xl md:text-8xl lg:text-9xl font-extrabold text-yellow-300 text-center animate-bounce drop-shadow-[0_0_40px_rgba(253,224,71,0.9)] leading-tight">
            We Are The Champions
          </div>
          <div
            dir="rtl"
            className="mt-6 text-5xl md:text-7xl font-extrabold text-white text-center drop-shadow-[0_0_30px_rgba(255,255,255,0.9)]"
            style={{ animation: 'championBlink 1s steps(1) infinite' }}
          >
            1,000,000 ש״ח
          </div>
        </div>

        {/* Click to close hint */}
        <div className="absolute bottom-8 inset-x-0 text-center text-white/60 text-sm pointer-events-none">
          לחץ לסגירה
        </div>
      </div>
    );
  }

  if (!data) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div
      className="fixed inset-0 z-50 pointer-events-auto cursor-pointer bg-black"
      onClick={dismiss}
    >
      {/* Background video — sits behind everything, full-bleed but contained
          to preserve the full image without cropping. Muted + playsInline so
          autoplay works in every browser and never conflicts with the TTS. */}
      <video
        src="/celebration.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-contain opacity-80"
      />
      {/* Subtle vignette to keep text legible over busy GIF frames. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50 pointer-events-none" />
      {/* Fireworks canvas — transparent background, painted on top of the GIF */}
      <canvas ref={canvasRef} className="absolute inset-0" />

      {/* Celebration content */}
      <div className="absolute inset-0 flex flex-col items-center pointer-events-none">
        {/* Agent name — pinned near the top with a small margin, slightly larger */}
        <div className="text-7xl md:text-9xl font-bold text-white mt-8 animate-bounce drop-shadow-[0_0_30px_rgba(255,255,255,0.8)]">
          {data.agentName}
        </div>

        {/* Empty middle so the video stays visible between the top name
            and the bottom content block */}
        <div className="flex-1" />

        {/* Bottom block: Amount → Message → Company, stacked above the close hint */}
        <div className="flex flex-col items-center gap-4 pb-20 max-w-2xl w-full mx-4">
          <div className="text-4xl md:text-6xl font-bold text-green-400 animate-pulse drop-shadow-[0_0_20px_rgba(74,222,128,0.8)]">
            {formatCurrency(data.amount)}
          </div>
          {data.message && (
            <div className="w-full p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
              <p className="text-xl md:text-2xl text-white text-center leading-relaxed" dir="rtl">
                {data.message}
              </p>
            </div>
          )}
          <div className="text-2xl md:text-3xl text-white/80">
            {data.companyName}
          </div>
        </div>

        {/* Click to close hint */}
        <div className="absolute bottom-8 text-white/60 text-sm">
          לחץ לסגירה
        </div>
      </div>
    </div>
  );
}
